import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { FundsSelectorApiClient } from '../api/fundsSelectorApiClient';
import { ThunkResult } from './index';

export interface StateShape {
    funds: any[];
    selectedFunds: string[];
    fundsLoading: boolean;
    fundsLoadError: any | null;
    hasMore: boolean;
    isSelectedAllFunds: boolean;
    selectedFundsValue: string;
    fundsTypes: string[];
}

export const fundsSelectorDefaultState: StateShape = {
    funds: [],
    selectedFunds: [],
    fundsLoading: false,
    fundsLoadError: null,
    hasMore: true,
    isSelectedAllFunds: false,
    selectedFundsValue: '',
    fundsTypes: [],
};

const apiClient = new FundsSelectorApiClient();

export const actionCreators = {
    listFunds: (
        searchString: string,
        page: number,
        limit: number,
        sortDirection: string,
        sortColumn: string,
        filter?: {
            fundType?: string[];
            riskRating?: number[];
            mer?: number[];
        }
    ): ThunkResult<Promise<boolean | void>> => {
        return async (dispatch, getState) => {
            const state = getState();
            const accessToken = state.auth.user?.access_token;
            dispatch(fundsSelectorReducerActions.FUNDS_LOAD_START());

            try {
                const funds = await apiClient.getFunds(
                    searchString,
                    page,
                    limit,
                    sortColumn,
                    sortDirection,
                    filter,
                    {
                        accessToken,
                    }
                );
                dispatch(fundsSelectorReducerActions.FUNDS_LOAD_END({ funds: funds }));
                return true;
            } catch (err: any) {
                console.error(err);
                dispatch(
                    fundsSelectorReducerActions.FUNDS_LOAD_END({
                        error: { message: err.message },
                        funds: [],
                    })
                );
                return false;
            }
        };
    },
    resetState: (isClearSelectedFunds?: boolean): ThunkResult<Promise<void>> => {
        return async (dispatch) => {
            dispatch(fundsSelectorReducerActions.RESET_STATE({ value: !!isClearSelectedFunds }));
        };
    },
    setSelectedFunds: (
        fundCode: string | null,
        isSelectAll?: boolean
    ): ThunkResult<Promise<void>> => {
        return async (dispatch) => {
            dispatch(fundsSelectorReducerActions.SET_SELECTED_FUNDS({ fundCode, isSelectAll }));
        };
    },
    setSelectedFundsValue:
        (value: string): ThunkResult<Promise<void>> =>
        async (dispatch) => {
            dispatch(fundsSelectorReducerActions.SET_SELECTED_FUNDS_VALUE({ value }));
        },
    getFundsTypes: (): ThunkResult<Promise<boolean | void>> => {
        return async (dispatch, getState) => {
            const state = getState();
            const accessToken = state.auth.user?.access_token;

            try {
                const funds = await apiClient.getFundsTypes({
                    accessToken,
                });
                dispatch(fundsSelectorReducerActions.FUNDS_TYPES_LOAD_END({ fundsTypes: funds }));
                return true;
            } catch (err: any) {
                console.error(err);
                dispatch(
                    fundsSelectorReducerActions.FUNDS_TYPES_LOAD_END({
                        fundsTypes: [],
                    })
                );
                return false;
            }
        };
    },
};

const fundsSelectorSlice = createSlice({
    name: 'fundsSelector',
    initialState: fundsSelectorDefaultState,
    reducers: {
        FUNDS_LOAD_START(draft) {
            draft.fundsLoading = true;
            draft.fundsLoadError = null;
        },
        FUNDS_LOAD_END(draft, action: PayloadAction<{ funds: any[]; error?: any }>) {
            const { funds, error } = action.payload;
            draft.fundsLoading = false;
            draft.funds = [...draft.funds, ...funds];
            if (funds.length === 0) {
                draft.hasMore = false;
            } else {
                draft.isSelectedAllFunds = false;
            }

            draft.fundsLoadError = error;
        },
        FUNDS_TYPES_LOAD_END(draft, action: PayloadAction<{ fundsTypes: any[] }>) {
            const { fundsTypes } = action.payload;
            draft.fundsTypes = [...draft.fundsTypes, ...fundsTypes];
        },
        RESET_STATE(draft, action: PayloadAction<{ value: Boolean }>) {
            draft.fundsLoading = true;
            draft.funds = [];
            draft.fundsLoadError = null;
            draft.hasMore = true;
            if (action.payload.value) {
                draft.selectedFunds = [];
            }
        },
        SET_SELECTED_FUNDS_VALUE(draft, action: PayloadAction<{ value: string }>) {
            draft.selectedFundsValue = action.payload.value;
            draft.fundsLoading = true;
            draft.funds = [];
            draft.fundsLoadError = null;
            draft.hasMore = true;
            draft.selectedFunds = [];
        },
        SET_SELECTED_FUNDS(
            draft,
            action: PayloadAction<{ fundCode: string | null; isSelectAll?: boolean }>
        ) {
            const { fundCode, isSelectAll } = action.payload;
            let selectedFunds = draft.selectedFunds;
            const funds = draft.funds;

            if (!fundCode) {
                if (!isSelectAll) {
                    selectedFunds = [];
                    draft.isSelectedAllFunds = false;
                } else {
                    selectedFunds = funds.map((d) => d.fundCode);
                    draft.isSelectedAllFunds = true;
                }
            } else {
                // Select or deselect a single fund code
                // Select or deselect a single fund code
                const index = selectedFunds.indexOf(fundCode);
                if (index > -1) {
                    selectedFunds.splice(index, 1);
                } else {
                    selectedFunds.push(fundCode);
                }
            }

            draft.selectedFunds = selectedFunds;
        },
    },
});

const fundsSelectorReducerActions = fundsSelectorSlice.actions;
export const fundsSelectorReducer = fundsSelectorSlice.reducer;
